/**
 * Category definitions and grouping utilities for list items.
 * Mirrors the category system used by the cart module.
 */
import { ListItem } from "modules/lists/types/lists";

export interface ListCategoryDefinition {
    id: number;
    name: string;
    icon: string;
    orderField: number;
}

export interface ListCategoryGroup {
    category: ListCategoryDefinition;
    items: ListItem[];
}

export const listCategoryDefinitions: ListCategoryDefinition[] = [
    { id: 1, name: "Ofertas", icon: "sale", orderField: 0 },
    { id: 5, name: "Despensa", icon: "store", orderField: 10 },
    { id: 6, name: "Lácteos", icon: "milk", orderField: 20 },
    { id: 9, name: "Panadera, Pasteleria", icon: "bread", orderField: 25 },
    { id: 20, name: "Quesos y Fiambres", icon: "cheese", orderField: 27 },
    { id: 10, name: "Dulces y Colaciones", icon: "candy", orderField: 28 },
    { id: 3, name: "Frutas y Verduras", icon: "fruit", orderField: 30 },
    { id: 2, name: "Carniceria", icon: "beef", orderField: 40 },
    { id: 15, name: "Congelados", icon: "frozen", orderField: 45 },
    { id: 4, name: "Bebés", icon: "baby", orderField: 50 },
    { id: 7, name: "Limpieza", icon: "clean", orderField: 60 },
    { id: 8, name: "Perfumería", icon: "persCare", orderField: 65 },
    { id: 12, name: "Botilleria", icon: "drink", orderField: 70 },
    { id: 11, name: "Mascotas", icon: "house", orderField: 80 },
];

const fallbackCategory: ListCategoryDefinition = {
    id: -1,
    name: "Otros",
    icon: "store",
    orderField: Number.MAX_SAFE_INTEGER,
};

/**
 * Resolves the display metadata for a category id.
 * Falls back to "Otros" for unknown ids.
 */
export const getListCategoryDefinition = (categoryId: number): ListCategoryDefinition =>
    listCategoryDefinitions.find((c) => c.id === categoryId) ?? {
        ...fallbackCategory,
        id: categoryId,
    };

/**
 * Groups a flat list of items by their categoryId and sorts groups
 * by the configured orderField.
 * Items with null categoryId are grouped under the fallback "Otros" category.
 */
export const groupListItemsByCategory = (items: ListItem[]): ListCategoryGroup[] => {
    const grouped = new Map<number, ListCategoryGroup>();

    items.forEach((item) => {
        const catId = item.categoryId ?? fallbackCategory.id;
        const definition = getListCategoryDefinition(catId);
        const existing = grouped.get(catId);

        if (existing) {
            existing.items.push(item);
        } else {
            grouped.set(catId, { category: definition, items: [item] });
        }
    });

    return Array.from(grouped.values()).sort(
        (a, b) => a.category.orderField - b.category.orderField,
    );
};
